import { X } from 'lucide-react';
import { useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import { IconButton } from './IconButton';
import { cx } from './shared';
import { useDialogFocus } from './useDialogFocus';

export interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: 'left' | 'right' | 'bottom';
  initialFocusRef?: RefObject<HTMLElement | null>;
}

const sideClass = {
  left: 'inset-y-0 left-0 h-full w-[min(24rem,calc(100%-2rem))]',
  right: 'inset-y-0 right-0 h-full w-[min(24rem,calc(100%-2rem))]',
  bottom: 'inset-x-0 bottom-0 max-h-[calc(100dvh-2rem)] w-full rounded-b-none',
};

export function Sheet({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  side = 'right',
  initialFocusRef,
}: SheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const titleId = `sheet-title-${generatedId}`;
  const descriptionId = description ? `sheet-description-${generatedId}` : undefined;

  useDialogFocus(isOpen, onClose, sheetRef, initialFocusRef);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div
        aria-hidden="true"
        className="fixed inset-0 z-50 bg-scrim"
        onMouseDown={onClose}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className={cx(
          'so-overlay-enter fixed z-50 flex flex-col overflow-hidden rounded-overlay border border-border-strong bg-surface-raised text-ink shadow-overlay',
          sideClass[side],
        )}
      >
        <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-field-gap border-b border-border-subtle bg-surface-raised px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-section-title font-semibold text-ink-strong">
              {title}
            </h2>
            {description ? (
              <div
                id={descriptionId}
                data-sheet-description
                className="mt-1 bg-surface-raised text-body text-ink-muted"
              >
                {description}
              </div>
            ) : null}
          </div>
          <IconButton label="Close sheet" onClick={onClose}>
            <X className="h-5 w-5" />
          </IconButton>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto bg-surface-raised p-5">
          {children}
        </div>
        {footer ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-inline-control-gap border-t border-border-subtle bg-surface-raised p-4">
            {footer}
          </div>
        ) : null}
      </div>
    </>,
    document.body,
  );
}
